import { Info } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PushPinIcon from '@mui/icons-material/PushPin';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Dayjs } from 'dayjs';
import {
  ManualCollectionNotification,
  StakeholderGroup,
} from 'lib/api/collectionNotifications/types';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatMoney } from 'lib/functions/utils';
import React from 'react';

import { STATUS_STYLES, StatusPills } from './components/StatusPills';

const StakeholderHeader = ({ group }: { group: StakeholderGroup }) => {
  const total = group.notifications.length;
  const name = group.stakeholderName || 'Sin nombre de deudor';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Box>
        <Typography variant="subtitle1" color="text.primary">
          {name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {total} gesti{total !== 1 ? 'ones' : 'ón'}
        </Typography>
      </Box>
      <StatusPills statusCount={group.statusCount} />
    </Box>
  );
};

const ManualNotificationItem = ({
  management,
}: {
  management: ManualCollectionNotification;
}) => {
  const description = management.description || '';
  const issuedDate = management.issuedDate
    ? String(management.issuedDate).slice(0, 10)
    : '-';
  const statusStyles = STATUS_STYLES[management.status];

  return (
    <ListItem
      key={management.id}
      secondaryAction={
        <LightTooltip
          placement="left"
          title={
            <div style={{ padding: '16px', minWidth: '300px' }}>
              <Grid container>
                <Grid item xs={6}>
                  <Typography fontWeight={600}>Ejecutivo</Typography>
                  <Typography>{management.collectorName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight={600}>Fecha emisión</Typography>
                  <Typography>{issuedDate}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight={600}>Folio</Typography>
                  <Typography>{management.folio}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography fontWeight={600}>Monto</Typography>
                  <Typography>{formatMoney(management.amount)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography fontWeight={600}>Emisor</Typography>
                  <Typography>{management.businessName}</Typography>
                </Grid>
              </Grid>
            </div>
          }
        >
          <IconButton edge="end" aria-label="ver más">
            <Info />
          </IconButton>
        </LightTooltip>
      }
    >
      <ListItemAvatar>
        <Box
          sx={{
            borderRadius: '999px',
            bgcolor: statusStyles.bgColor,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LightTooltip
            title={
              <Typography style={{ padding: '4px' }}>
                {management.status}
              </Typography>
            }
          >
            <PushPinIcon
              sx={{
                color: statusStyles.textColor,
                fontSize: 18,
              }}
            />
          </LightTooltip>
        </Box>
      </ListItemAvatar>
      <ListItemText
        primary={
          <LightTooltip
            title={
              <div style={{ padding: '4px', maxWidth: '500px' }}>
                <Typography style={{ padding: '4px' }}>
                  {description || 'Sin descripción'}
                </Typography>
              </div>
            }
          >
            <span>
              {description
                ? description.length > 70
                  ? `${description.slice(0, 70)}...`
                  : description
                : 'Sin descripción'}
            </span>
          </LightTooltip>
        }
      />
    </ListItem>
  );
};

const StakeholderSkeleton = () => (
  <Accordion
    elevation={0}
    square
    disableGutters
    sx={{
      '&::before': {
        display: 'none',
      },
      borderRadius: 2,
      mb: 1,
      bgcolor: 'transparent',
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box>
          <Skeleton variant="text" width={180} height={24} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={120} height={16} />
        </Box>
        <Stack direction="row" spacing={1}>
          <Skeleton
            variant="rectangular"
            width={32}
            height={24}
            sx={{ borderRadius: '999px' }}
          />
          <Skeleton
            variant="rectangular"
            width={32}
            height={24}
            sx={{ borderRadius: '999px' }}
          />
        </Stack>
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      <List>
        {[...Array(2)].map((_, index) => (
          <ListItem key={index}>
            <ListItemAvatar>
              <Skeleton variant="circular" width={32} height={32} />
            </ListItemAvatar>
            <ListItemText
              primary={<Skeleton variant="text" width="80%" height={20} />}
              secondary={<Skeleton variant="text" width="60%" height={16} />}
            />
            <IconButton edge="end" disabled>
              <Skeleton variant="circular" width={24} height={24} />
            </IconButton>
          </ListItem>
        ))}
      </List>
    </AccordionDetails>
  </Accordion>
);

export const ManualSummary = (props: {
  selectedDate: Dayjs;
  filteredManualNotifications: StakeholderGroup[];
  loading?: boolean;
}) => {
  const { selectedDate, filteredManualNotifications, loading = false } = props;

  return (
    <div>
      <Typography variant="h5">
        Gestiones para {selectedDate.format('DD/MM/YYYY')}
      </Typography>
      <Divider />
      {loading ? (
        <>
          {[...Array(3)].map((_, index) => (
            <StakeholderSkeleton key={index} />
          ))}
        </>
      ) : (
        <>
          {filteredManualNotifications.length === 0 && (
            <Typography variant="body1">
              No hay gestiones para esta fecha.
            </Typography>
          )}
          {filteredManualNotifications.map((group) => (
            <Accordion
              key={group.stakeholderName || 'sin-stakeholder'}
              elevation={0}
              square
              disableGutters
              sx={{
                '&::before': {
                  display: 'none',
                },
                borderRadius: 2,
                mb: 1,
                bgcolor: 'transparent',
                transition: 'background-color 0.15s ease-in-out',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.02)',
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <StakeholderHeader group={group} />
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {group.notifications.map((management) => (
                    <ManualNotificationItem
                      key={management.id}
                      management={management}
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </div>
  );
};

export default ManualSummary;
